import {
  BILLING_DISPLAY,
  CONTRACT_DISPLAY,
  billingFor,
  contractBadge,
  isCollectible,
  resolveContract,
} from '@/config/contract';
import type { ClientConfig, ClientMetadata } from '@/types';

type Metadata = Pick<ClientMetadata, 'contract' | 'partner' | 'trialEndDate'> | undefined;
type Config = Pick<ClientConfig, 'contractType'> | undefined;

/**
 * The whole contract in words: "Credits", "Trial, expired", "Partner: Ohnyx,
 * billed as Legacy". Undefined when no contract is set.
 */
export function contractText(metadata: Metadata, config: Config): string | undefined {
  const contract = resolveContract(metadata, config);
  if (!contract) return undefined;
  if (contract === 'partner') {
    const who = metadata?.partner ? `Partner: ${metadata.partner}` : 'Partner';
    return `${who}, billed as ${BILLING_DISPLAY[billingFor('partner', config?.contractType)]}`;
  }
  return contractBadge(metadata, config)?.label ?? CONTRACT_DISPLAY[contract].label;
}

/**
 * Why the client is not billed for the credits it uses past its monthly
 * credits, in one line. Null when it is billed (`isCollectible`).
 */
export function notBilledText(metadata: Metadata, config: Config, inactive: boolean): string | null {
  const contract = resolveContract(metadata, config);
  if (isCollectible(contract, config?.contractType, inactive)) return null;
  if (inactive) return 'Not billed for credits: the client is marked inactive';
  if (!contract) return 'Not billed for credits: no contract is set';
  if (contract === 'partner') {
    const billing = BILLING_DISPLAY[billingFor('partner', config?.contractType)];
    return `Not billed for credits: partner contract${metadata?.partner ? ` with ${metadata.partner}` : ''}, billed as ${billing}`;
  }
  return `Not billed for credits: ${CONTRACT_DISPLAY[contract].label} contract`;
}
